"use client";

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Chart from 'chart.js/auto';
import { Footer } from '@/components/Footer';

// Data Definitions
const healthData = {
    brain: { title: "สมอง", desc: "เพิ่มความเสี่ยงอัมพฤกษ์ อัมพาต 2-4 เท่า เนื่องจากเส้นเลือดตีบและสมองขาดออกซิเจน", risk: 80 },
    lungs: { title: "ปอด", desc: "เป็นสาเหตุของมะเร็งปอดและถุงลมโป่งพอง 80% ของเคสผู้ป่วยทั้งหมด ปอดจะดำและสูญเสียความยืดหยุ่น", risk: 95 },
    heart: { title: "หัวใจ", desc: "สารนิโคตินทำให้หัวใจเต้นเร็วและบีบตัวแรงขึ้น เสี่ยงต่อหัวใจวายเฉียบพลันและโรคหลอดเลือดหัวใจ", risk: 75 },
    skin: { title: "ผิวหนัง/ช่องปาก", desc: "ผิวเหี่ยวแห้งก่อนวัย ฟันดำ มีกลิ่นปากรุนแรง และเสี่ยงมะเร็งในช่องปากและกล่องเสียง", risk: 65 }
};

const timelineData = [
    { title: "20 นาที", desc: "ความดันโลหิตและชีพจรลดลงสู่ระดับปกติ มือและเท้าเริ่มอุ่นขึ้น", badge: "ทันที", lung: 5, heart: 10 },
    { title: "12 ชั่วโมง", desc: "ระดับคาร์บอนมอนอกไซด์ในเลือดลดลงสู่ระดับปกติ ออกซิเจนในเลือดเพิ่มขึ้น", badge: "ผ่านวันแรก", lung: 15, heart: 20 },
    { title: "2 สัปดาห์ - 3 เดือน", desc: "การไหลเวียนเลือดดีขึ้นมาก การทำงานของปอดเริ่มฟื้นตัว", badge: "ระยะสั้น", lung: 40, heart: 35 },
    { title: "1 - 9 เดือน", desc: "อาการไอและเหนื่อยหอบลดลง ขนกวัดในปอดทำงานปกติเพื่อกำจัดเสมหะ", badge: "ระยะกลาง", lung: 65, heart: 55 },
    { title: "1 ปี", desc: "ความเสี่ยงต่อโรคหลอดเลือดหัวใจลดลงเหลือครึ่งหนึ่งของคนสูบ", badge: "เป้าหมายใหญ่", lung: 80, heart: 75 },
    { title: "5 ปี", desc: "ความเสี่ยงเส้นเลือดในสมองแตกเทียบเท่าคนปกติ มะเร็งปาก/คอลดลงครึ่งหนึ่ง", badge: "ระยะยาว", lung: 90, heart: 85 },
    { title: "10-15 ปี", desc: "เสมือนคนไม่เคยสูบ ความเสี่ยงมะเร็งปอดและโรคหัวใจลดลงเท่าคนปกติ", badge: "ชีวิตใหม่", lung: 100, heart: 100 }
];

type OrganType = keyof typeof healthData;

export function HealthReport() {
    const riskRadarRef = useRef<HTMLCanvasElement>(null);
    const savingsChartRef = useRef<HTMLCanvasElement>(null);
    const riskRadarInstance = useRef<Chart | null>(null);
    const savingsChartInstance = useRef<Chart | null>(null);

    // State
    const [activeOrgan, setActiveOrgan] = useState<OrganType | null>(null);
    const [activeTimelineIndex, setActiveTimelineIndex] = useState(0);
    const [cigsPerDay, setCigsPerDay] = useState(10);
    const [pricePerPack, setPricePerPack] = useState(100);
    const [savingsPerYear, setSavingsPerYear] = useState(0);

    // Initial Chart Render (Radar)
    useEffect(() => {
        if (riskRadarInstance.current) {
            riskRadarInstance.current.destroy();
        }

        if (riskRadarRef.current) {
            const ctx = riskRadarRef.current.getContext('2d');
            if (ctx) {
                riskRadarInstance.current = new Chart(ctx, {
                    type: 'radar',
                    data: {
                        labels: ['มะเร็งปอด', 'โรคหัวใจ', 'เส้นเลือดสมอง', 'ถุงลมโป่งพอง', 'ริ้วรอย', 'มะเร็งช่องปาก'],
                        datasets: [{
                            label: 'ผู้สูบ',
                            data: [95, 80, 85, 90, 70, 85],
                            backgroundColor: 'rgba(239, 68, 68, 0.2)',
                            borderColor: '#EF4444',
                            pointBackgroundColor: '#EF4444'
                        }, {
                            label: 'ไม่สูบ',
                            data: [10, 15, 10, 5, 20, 10],
                            backgroundColor: 'rgba(16, 185, 129, 0.2)',
                            borderColor: '#10B981',
                            pointBackgroundColor: '#10B981'
                        }]
                    },
                    options: {
                        maintainAspectRatio: false,
                        scales: {
                            r: {
                                suggestedMin: 0,
                                suggestedMax: 100,
                                ticks: { display: false },
                                grid: { color: 'rgba(0,0,0,0.05)' }
                            }
                        },
                        plugins: {
                            legend: { position: 'bottom' }
                        }
                    }
                });
            }
        }

        return () => {
            if (riskRadarInstance.current) {
                riskRadarInstance.current.destroy();
            }
        };
    }, []);

    // Savings Calculation & Chart Update
    useEffect(() => {
        const perYear = (cigsPerDay / 20) * pricePerPack * 365;
        setSavingsPerYear(Math.round(perYear));

        const dataPoints = [1, 3, 5, 10].map(y => y * perYear);

        if (savingsChartRef.current) {
            const ctx = savingsChartRef.current.getContext('2d');
            if (ctx) {
                if (savingsChartInstance.current) {
                    savingsChartInstance.current.data.datasets[0].data = dataPoints;
                    savingsChartInstance.current.update();
                } else {
                    savingsChartInstance.current = new Chart(ctx, {
                        type: 'bar',
                        data: {
                            labels: ['1 ปี', '3 ปี', '5 ปี', '10 ปี'],
                            datasets: [{
                                label: 'เงินออมสะสม',
                                data: dataPoints,
                                backgroundColor: '#10B981',
                                borderRadius: 10,
                                hoverBackgroundColor: '#059669'
                            }]
                        },
                        options: {
                            maintainAspectRatio: false,
                            plugins: { legend: { display: false } },
                            scales: {
                                y: { display: false },
                                x: {
                                    grid: { display: false },
                                    ticks: { color: '#9ca3af' }
                                }
                            }
                        }
                    });
                }
            }
        }

        // Return cleanup function to destroy chart on unmount
        return () => {
            // In stricter React environments (like Next.js dev), components mount/unmount rapidly.
            // We generally want to persist the instance if just updating data, 
            // BUT if the component is truly unmounting, we must destroy.
            // Since we have logic to reuse `savingsChartInstance.current` inside the effect,
            // we can't blindly destroy it here without losing the reference for the update logic.
            // However, for correct HMR and strict mode, we SHOULD destroy and recreate if refs change.
            // Given the update logic handles "create if not exists", we CAN destroy it here to be safe,
            // forcing a recreate next render. This is safer for "Canvas is already in use".
            if (savingsChartInstance.current) {
                savingsChartInstance.current.destroy();
                savingsChartInstance.current = null;
            }
        };
    }, [cigsPerDay, pricePerPack]);

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-[#F9FAFB] font-[family-name:var(--font-sarabun)] text-gray-800">
            {/* Navigation */}
            <nav className="bg-white shadow-sm sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16">
                        <div className="flex items-center">
                            <Link href="/" className="flex items-center hover:opacity-80 transition-opacity">
                                <span className="text-2xl text-emerald-600 mr-2">◉</span>
                                <span className="font-bold text-xl tracking-tight text-gray-800">SmokeFree Hub</span>
                            </Link>
                        </div>
                        <div className="hidden md:flex space-x-6 items-center">
                            <button onClick={() => scrollToSection('health-impact')} className="text-sm font-medium text-gray-600 hover:text-emerald-600 transition-colors">ผลกระทบ</button>
                            <button onClick={() => scrollToSection('timeline')} className="text-sm font-medium text-gray-600 hover:text-emerald-600 transition-colors">ไทม์ไลน์เลิกบุหรี่</button>
                            <button onClick={() => scrollToSection('calculator')} className="text-sm font-medium text-gray-600 hover:text-emerald-600 transition-colors">คำนวณเงินออม</button>
                            <button onClick={() => scrollToSection('sources')} className="text-sm font-medium text-gray-600 hover:text-emerald-600 transition-colors">แหล่งข้อมูล</button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <header className="bg-gradient-to-r from-gray-50 to-emerald-50 py-20 border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <h1 className="text-4xl font-extrabold text-gray-900 sm:text-6xl mb-6">
                        ชีวิตใหม่ <span className="text-emerald-600">เริ่มได้ทันที</span>
                    </h1>
                    <p className="mt-6 text-lg text-gray-600 max-w-2xl mx-auto mb-10">
                        เรียนรู้ผลกระทบของการสูบบุหรี่ผ่านข้อมูลเชิงลึก และค้นพบว่าร่างกายของคุณฟื้นฟูได้เร็วแค่ไหนเมื่อตัดสินใจเลิกในวันนี้
                    </p>

                    {/* Audio Player */}
                    <div className="mb-10 flex flex-col items-center animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
                        <div className="bg-white/80 backdrop-blur-sm p-4 rounded-2xl border border-emerald-200 shadow-xl max-w-md w-full transform hover:scale-105 transition-transform duration-300">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-green-600 rounded-full flex items-center justify-center text-white shadow-lg shadow-emerald-200">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                                </div>
                                <div className="text-left">
                                    <h3 className="text-sm font-bold text-gray-900">ฟังรายงานสรุป</h3>
                                    <p className="text-xs text-gray-500">Audio Briefing (3 mins)</p>
                                </div>
                            </div>
                            <audio controls className="w-full h-10 accent-emerald-600 rounded-lg">
                                <source src="/Smoking_Effects_And_Benefits_Report.mp3" type="audio/mpeg" />
                                Your browser does not support the audio element.
                            </audio>
                        </div>
                    </div>
                    <div className="flex flex-wrap justify-center gap-4">
                        <button onClick={() => scrollToSection('timeline')} className="px-8 py-4 bg-emerald-600 text-white font-bold rounded-xl shadow-lg hover:bg-emerald-700 transition active:scale-95">
                            แผนภูมิการฟื้นฟู
                        </button>
                        <button onClick={() => scrollToSection('health-impact')} className="px-8 py-4 bg-white text-gray-700 font-bold rounded-xl border border-gray-200 hover:bg-gray-50 transition active:scale-95">
                            ผลกระทบต่อร่างกาย
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-grow">
                {/* Section: Health Impact */}
                <section id="health-impact" className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="mb-16 text-center">
                            <h2 className="text-3xl font-bold text-gray-900">ผลกระทบต่อสุขภาพที่มองไม่เห็น</h2>
                            <p className="text-gray-500 mt-2">สำรวจความเสี่ยงที่ซ่อนอยู่ในแต่ละส่วนของร่างกาย</p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    {(Object.entries(healthData) as [OrganType, typeof healthData[OrganType]][]).map(([key, data]) => (
                                        <button
                                            key={key}
                                            onClick={() => setActiveOrgan(key)}
                                            className={`p-5 border rounded-2xl transition-all text-left group ${activeOrgan === key ? 'border-red-500 bg-red-50 ring-1 ring-red-500' : 'border-gray-100 bg-gray-50 hover:border-red-300'}`}
                                        >
                                            <span className="text-3xl block mb-2 group-hover:scale-110 transition-transform duration-300">
                                                {key === 'brain' && '🧠'}
                                                {key === 'lungs' && '🫁'}
                                                {key === 'heart' && '🫀'}
                                                {key === 'skin' && '🙂'}
                                            </span>
                                            <span className="font-bold text-gray-800">{data.title}</span>
                                        </button>
                                    ))}
                                </div>
                                <div className={`p-6 rounded-2xl min-h-[150px] transition-all duration-300 border ${activeOrgan ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100 opacity-60'}`}>
                                    {activeOrgan ? (
                                        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                                            <h4 className="text-xl font-bold text-red-700 mb-2">{healthData[activeOrgan].title}</h4>
                                            <p className="text-gray-700 leading-relaxed">{healthData[activeOrgan].desc}</p>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center h-full text-center py-4">
                                            <h4 className="text-lg font-bold text-gray-400 mb-1">เลือกส่วนของร่างกายด้านบน</h4>
                                            <p className="text-sm text-gray-400">เพื่อดูผลกระทบของสารพิษต่ออวัยวะนั้นๆ</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="bg-gray-50 p-8 rounded-3xl border border-gray-100 relative">
                                <h3 className="text-center font-bold mb-4 text-gray-700">เปรียบเทียบระดับความเสี่ยง</h3>
                                <div className="relative w-full h-[300px] md:h-[350px]">
                                    <canvas ref={riskRadarRef}></canvas>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section: Recovery Timeline */}
                <section id="timeline" className="py-20 bg-emerald-50">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl font-bold text-gray-900">ไทม์ไลน์การฟื้นตัวของร่างกาย</h2>
                            <p className="text-gray-500 mt-2">ร่างกายเริ่มซ่อมแซมตัวเองได้ทันทีหลังจากมวนสุดท้าย</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="space-y-2 h-[400px] overflow-y-auto custom-scroll pr-2 md:pr-4">
                                {timelineData.map((item, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setActiveTimelineIndex(idx)}
                                        className={`w-full p-4 rounded-xl border text-left font-bold transition-all ${idx === activeTimelineIndex
                                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-md transform scale-[1.02]'
                                            : 'bg-white text-gray-600 border-emerald-100 hover:bg-emerald-50'
                                            }`}
                                    >
                                        {item.title}
                                    </button>
                                ))}
                            </div>
                            <div className="md:col-span-2 bg-white p-8 md:p-10 rounded-3xl shadow-xl border border-emerald-100 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-50 rounded-full blur-3xl -z-0 translate-x-1/2 -translate-y-1/2"></div>
                                <div className="relative z-10 animate-in fade-in duration-500" key={activeTimelineIndex}>
                                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold uppercase mb-4 inline-block tracking-wider">
                                        {timelineData[activeTimelineIndex].badge}
                                    </span>
                                    <h3 className="text-2xl font-bold mb-4 text-gray-800">
                                        {timelineData[activeTimelineIndex].title}
                                    </h3>
                                    <p className="text-gray-600 text-lg leading-relaxed mb-8 min-h-[3.5rem]">
                                        {timelineData[activeTimelineIndex].desc}
                                    </p>
                                    <div className="space-y-6">
                                        <div>
                                            <div className="flex justify-between text-sm mb-2">
                                                <span className="font-bold text-gray-700">การฟื้นฟูปอด</span>
                                                <span className="font-bold text-emerald-600">{timelineData[activeTimelineIndex].lung}%</span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                                                <div
                                                    className="bg-emerald-500 h-3 rounded-full transition-all duration-1000 ease-out"
                                                    style={{ width: `${timelineData[activeTimelineIndex].lung}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex justify-between text-sm mb-2">
                                                <span className="font-bold text-gray-700">สุขภาพหัวใจ</span>
                                                <span className="font-bold text-blue-600">{timelineData[activeTimelineIndex].heart > 90 ? 'สมบูรณ์' : 'กำลังดีขึ้น'} ({timelineData[activeTimelineIndex].heart}%)</span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                                                <div
                                                    className="bg-blue-500 h-3 rounded-full transition-all duration-1000 ease-out"
                                                    style={{ width: `${timelineData[activeTimelineIndex].heart}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section: Calculator */}
                <section id="calculator" className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="bg-slate-900 rounded-[3rem] p-8 lg:p-16 text-white shadow-2xl relative overflow-hidden">
                            {/* Decor */}
                            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                                <div className="absolute top-[-100px] right-[-100px] w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[100px]"></div>
                                <div className="absolute bottom-[-100px] left-[-100px] w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[100px]"></div>
                            </div>

                            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12">
                                <div>
                                    <h2 className="text-3xl font-bold mb-6">คำนวณเงินออมในอนาคต</h2>
                                    <p className="text-gray-400 mb-8 leading-relaxed">เปลี่ยนเงินที่เสียไปกับควันบุหรี่ ให้กลายเป็นเงินออมเพื่อความฝันของคุณ เพียงแค่หยุดวันนี้</p>
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-sm text-gray-400 mb-2 font-medium">สูบบุหรี่วันละกี่มวน?</label>
                                            <input
                                                type="number"
                                                value={cigsPerDay}
                                                onChange={(e) => setCigsPerDay(Math.max(0, parseInt(e.target.value) || 0))}
                                                className="w-full bg-slate-800 border-none rounded-xl p-4 text-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all placeholder:text-gray-600"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm text-gray-400 mb-2 font-medium">ราคาต่อซอง (บาท)</label>
                                            <input
                                                type="number"
                                                value={pricePerPack}
                                                onChange={(e) => setPricePerPack(Math.max(0, parseInt(e.target.value) || 0))}
                                                className="w-full bg-slate-800 border-none rounded-xl p-4 text-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all placeholder:text-gray-600"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10">
                                    <div className="text-center mb-8">
                                        <span className="text-xs text-emerald-400 font-bold uppercase tracking-widest block mb-2">เงินที่คุณจะประหยัดได้ต่อปี</span>
                                        <div className="text-5xl font-extrabold text-white tracking-tight">
                                            {savingsPerYear.toLocaleString()} <span className="text-2xl text-gray-400 font-medium">฿</span>
                                        </div>
                                    </div>
                                    <div className="relative w-full h-[250px]">
                                        <canvas ref={savingsChartRef}></canvas>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section: Sources & References */}
                <section id="sources" className="py-20 bg-gray-50 border-t border-gray-200">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="mb-12">
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">แหล่งข้อมูลและอ้างอิง</h2>
                            <p className="text-gray-600">ข้อมูลทั้งหมดในหน้านี้รวบรวมและสรุปจากองค์กรด้านสุขภาพที่ได้มาตรฐานสากลและในประเทศไทย</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[
                                { name: "องค์การอนามัยโลก (WHO)", desc: "รายงานสถิติยาสูบโลกและผลกระทบเชิงระบาดวิทยาต่อสุขภาพประชากร", link: "https://www.who.int/news-room/fact-sheets/detail/tobacco", linkText: "อ่านเพิ่มเติม" },
                                { name: "สำนักงานกองทุน สสส.", desc: "ข้อมูลโครงการรณรงค์เพื่อการไม่สูบบุหรี่และสถิติผู้สูบบุหรี่ในประเทศไทย", link: "https://www.thaihealth.or.th", linkText: "เยี่ยมชมเว็บไซต์" },
                                { name: "CDC (สหรัฐอเมริกา)", desc: "ข้อมูลเชิงลึกเกี่ยวกับไทม์ไลน์การฟื้นตัวของร่างกายหลังหยุดสูบบุหรี่", link: "https://www.cdc.gov/tobacco/quit_smoking/index.htm", linkText: "ข้อมูลการเลิกบุหรี่" },
                                { name: "มูลนิธิรณรงค์เพื่อการไม่สูบบุหรี่", desc: "สื่อความรู้เรื่องสารเคมีในบุหรี่และผลกระทบต่ออวัยวะต่างๆ", link: "http://www.ashthailand.or.th", linkText: "คลังความรู้" },
                                { name: "คลินิกฟ้าใส", desc: "แนวทางการรักษาและการใช้ยาเพื่อช่วยเลิกบุหรี่อย่างถูกวิธีในไทย", link: "https://www.thailandquitline.or.th", linkText: "ติดต่อปรึกษา" }
                            ].map((source, i) => (
                                <div key={i} className="bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition duration-300">
                                    <div className="font-bold text-lg text-emerald-700 mb-2">{source.name}</div>
                                    <p className="text-sm text-gray-600 mb-4 opacity-80">{source.desc}</p>
                                    <a href={source.link} target="_blank" rel="noopener noreferrer" className="text-emerald-600 text-sm font-bold hover:underline inline-flex items-center gap-1 group">
                                        {source.linkText} <span className="group-hover:translate-x-1 transition-transform">→</span>
                                    </a>
                                </div>
                            ))}

                            {/* Hotline Box */}
                            <div className="bg-emerald-600 p-6 rounded-2xl text-white shadow-lg shadow-emerald-600/20">
                                <div className="font-bold text-lg mb-2">สายด่วนเลิกบุหรี่ 1600</div>
                                <p className="text-sm text-emerald-50 opacity-90 mb-6">ปรึกษาฟรี ไม่มีค่าใช้จ่าย พร้อมเจ้าหน้าที่ให้คำแนะนำตลอดเวลาทำการ</p>
                                <a href="tel:1600" className="inline-block px-5 py-2.5 bg-white text-emerald-600 rounded-lg font-bold hover:bg-emerald-50 transition shadow-sm active:scale-95">
                                    โทรเลย 1600
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer theme="light" />
        </div>
    );
}

export default HealthReport;
